import React, { useEffect, useRef, useState } from 'react';
import './quest-form.styles.scss';
import ImageContainer from '@/components/SHARED/image-container/image-container.component';
import { createPath, useNavigate, useParams } from 'react-router-dom';
import ManageContentInformation from '../blocks/information.component';
import QuestMapCreator from './quest-map-creator.component';
import { ModalAlert, ModalConfirm } from '@/components/modal/modal.component';
import ShowHideSettingsButton from '../blocks/show-hide-button.component';
import ManageContentVisibility from '../blocks/visibility.component';
import ManageContentPrerequisites from '../blocks/prerequisites.component';
import ManageContentRewards from '../blocks/rewards.component';
import { UseCloudFunction } from '@/utils/firebase.utils';
import { createPathFromTimestamp } from '@/utils/createPathFromTimestamp';
import { useSelector } from 'react-redux';
import ManageContentIconCropped from '../blocks/icon.component';
import { getOptimizedBlob, getOptimizedImageNoCrop } from '@/utils/getOptimizedBlob';
import { getDownloadURL, getStorage, ref, uploadBytes } from 'firebase/storage';
import FullPageLoader from '@/components/loader/full-page-loader.component';
import useAnalytics from '@/utils/useAnalytics';
import ManageContentCategories from '../blocks/categories.component';
import BackButton from '@/components/SHARED/back-button/back-button.component';
import ManageContentDates from '../blocks/dates.component';

const QuestForm = ({
  gameData,
  gameElements,
  playerList,
  questsFromDb
}) => {

  const userProfile = useSelector(state => state.userData.userProfile);
  const membership = useSelector(state => state.userData.membership);
  const [formData, setFormData] = useState({});
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState(null);
  const { trackEvent, trackException } = useAnalytics();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const { elementPath } = useParams();
  const dataRef = useRef({
    'opt_visible': false
  });
  const approveRef = useRef();
  const navigate = useNavigate();
  const storage = getStorage();

  useEffect(() => {
    if (elementPath && questsFromDb) {
      const quest = Object.values(questsFromDb).find(q => q.path === elementPath);
      if (quest) {
        setFormData(quest);
        dataRef.current = quest;
      } else {
        setAlertMessage('Could not find that Quest. Returning to Quests list.');
        approveRef.current = () => {
          navigate(`/games/${gameData.gameId}/elements/quests`);
        }
      }
    } else {
      setFormData({
        'opt_visible': false
      });
      dataRef.current = {
        'opt_visible': false
      }
    }
  }, [elementPath, questsFromDb])

  useEffect(() => {
    if (!questsFromDb) return;
    const cats = new Set(Object.values(questsFromDb)
      .map(el => el.opt_cat ? el.opt_cat : null));
    setCategories([...cats]);
  }, [questsFromDb])

  function saveData(key, value) {
    dataRef.current = {
      ...dataRef.current,
      [key]: value
    }
    setFormData(dataRef.current);
    // check if checkpoints are complete; if not, set visibility to false

    if (key === 'opt_visible' || dataRef.current.opt_visible) {
      const isIncomplete =
        !dataRef.current.checkpoints ||
        dataRef.current.checkpoints.length === 0 ||
        dataRef.current.checkpoints.some(cp => cp.status === 'incomplete');
      if (isIncomplete) {
        setAlertMessage('Quests with incomplete or no checkpoints cannot be visible to players. Please finish setting up all checkpoints before making this quest visible.');
        dataRef.current.opt_visible = false;
        setFormData(dataRef.current);
      }
    }
  }

  function handleSaveQuest() {
    let tempData = { ...dataRef.current };
    console.log(tempData);
    const req = ['name', 'icon'];
    for (let i = 0; i < req.length; i++) {
      const key = req[i];
      if (!tempData[key]) {
        setAlertMessage(`Please fill in the required field: ${key}`);
        return;
      }
    }
    const hasCheckpoints = tempData.checkpoints && tempData.checkpoints.length > 0;
    if (!hasCheckpoints) {
      setAlertMessage('Please add at least one checkpoint to this quest before saving.');
      return;
    }
    const isIncomplete = tempData.checkpoints.some(cp => cp.status === 'incomplete');
    approveRef.current = () => continueSave();
    if (isIncomplete) {
      setConfirmMessage('At least one of your checkpoints is incomplete. Your current progress will be saved, ' +
        'but the quest will not be visible to your players until you have ' +
        'finished setting up all existing checkpoints. <br /><br /><b>NOTE:</b> The Quest will appear under the <b>"HIDDEN"</b> filter ' +
        'in your Quests list until all checkpoints are complete and visibility is toggled on.');
    } else {
      setConfirmMessage('Are you sure you want to save this Quest? You can always come back and edit it later.');
    }
  }

  async function continueSave() {
    approveRef.current = null;
    setLoading(true);
    let tempData = { ...dataRef.current }
    if (tempData.icon.upload) {
      const optimizedBlob = await getOptimizedBlob(tempData.icon.croppie, 20);
      console.log('Cropped image size in KB:', optimizedBlob.size / 1024);
      uploadImage(tempData.icon, optimizedBlob);
    } else if (tempData.icon.url) {
      finalizeSave(tempData.icon.url)
      return;
    } else if (tempData.icon) {
      finalizeSave(tempData.icon)
      return;
    } else {
      setAlertMessage('Something went wrong with the icon. Please try using a different image.');
      setLoading(false);
      return;
    }
  }

  async function uploadImage(icon, optimizedBlob) {
    const now = new Date().getTime();
    const path = `${userProfile.appId}/${now}-${icon.name}`;
    const iconRef = ref(storage, path)
    const metadata = {
      contentType: icon.type,
      customMetadata: {
        appId: userProfile.appId,
        email: userProfile.email
      }
    };
    console.log('Cropped image size in KB:', optimizedBlob.size / 1024);
    uploadBytes(iconRef, optimizedBlob, metadata).then((snapshot) => {
      getDownloadURL(iconRef)
        .then(async (url) => {
          finalizeSave(url);
          try {
            await UseCloudFunction(
              'saveToMyContent',
              {
                'gameId': gameData.gameId,
                'appId': userProfile.appId,
                'source': 'Quest Icons',
                'category': 'Images',
                'cropped': false,
                'contentData': {
                  'name': icon.name,
                  'url': url,
                  'type': 'image',
                  'id': `${now}_${RandomString(10)}_image`
                },
              }
            )
          } catch (error) {
            console.log('Error saving to My Content: ', error);
            trackException('Quest Icon Save to My Content Error: ' + error.message);
          }
          trackEvent(
            'image_saved_to_my_content',
            'Game Manager',
            `Quest icon ${icon.name} was uploaded for user ${userProfile.appId}.`,
            null,
            membership
          )
          return;
        })
    })
  }

  async function finalizeSave(iconUrl) {
    let tempData = { ...dataRef.current };
    const now = new Date().getTime();
    const gameCode = gameData.gameId.substring(gameData.gameId.indexOf('-') - 5, gameData.gameId.indexOf('-') + 6);
    let action = 'Edited';
    if (!tempData.id) {
      tempData = {
        ...tempData,
        ...{
          appId: userProfile.appId,
          gameId: gameData.gameId,
          id: now + '-' + gameCode + '-quest',
          status: 'active',
          type: 'quest',
          path: createPathFromTimestamp(),
        }
      }
      action = 'Created';
    }
    tempData.icon = iconUrl;
    tempData.opt_available = true;
    const res = await UseCloudFunction(
      'saveGameElement',
      {
        'gameId': gameData.gameId,
        'appId': userProfile.appId,
        'elementData': tempData,
        'userName': `${userProfile.fName} ${userProfile.lName}`,
        'action': action
      }
    )
    console.log(res);
    setLoading(false);
    if (res.error) {
      setAlertMessage('There was an error saving your quest: ' + res.error);
      return;
    }
    navigate(`/manage/${gameData.path}/elements/quests`);
    return;
  }

  return (
    <div className='quest-form'>
      <div className='quest-form-card g-card'>
        <BackButton cancel={() => navigate(-1)} />
        <div className="g-space-1"></div>
        <div className='card-title'>
          <ImageContainer
            src='/images/icons/quests.png'
            alt='quest icon'
            className='head-icon' />
          {
            elementPath ? "Edit" : "Create"
          } a Quest
        </div>
        <hr />
        <div className='quest-form-content'>
          <ManageContentInformation
            type='quest'
            saveData={saveData}
            formData={formData}
            textEditorLabel='Quest Explanation (optional)' />
          <div className='g-space-2'></div>
          <ManageContentIconCropped
            type='quest'
            saveData={saveData}
            formData={formData} />
          <div className='g-space-2'></div>
          <QuestMapCreator
            gameData={gameData}
            saveData={saveData}
            formData={formData}
            gameElements={gameElements} />
          <div className='g-space-2'></div>
          <ManageContentVisibility
            type="quest"
            saveData={saveData}
            formData={formData}
            previewText='Allow players to see this Quest before they earn it. (If toggled off, players will see a Mystery Quest.)'
            visibilityText="Turn visibility ON for this Quest. If toggled off, it will be totally hidden from players. 
            NOTE: Quests cannot be visible if they have incomplete checkpoints."
          />
          <div className='g-space-1'></div>
          <ShowHideSettingsButton
            showAdvancedOptions={showAdvancedOptions}
            setShowAdvancedOptions={setShowAdvancedOptions} />
          <div className='g-space-1'></div>
          <div className={`aditional-options ${showAdvancedOptions
            ? 'show'
            : 'hide'
            }`}>
            <ManageContentCategories
              type="quest"
              saveData={saveData}
              formData={formData}
              categories={categories} />
            <div className='g-space-2'></div>
            <ManageContentPrerequisites
              type="quest"
              saveData={saveData}
              formData={formData}
              gameElements={gameElements}
              availableItems={gameElements &&
                Object.values(gameElements)
                  .filter(el =>
                    el.type === 'item' &&
                    el.opt_itemType === 'inventory' &&
                    el.status === 'active'
                  )
              }
              teams={playerList ? Object.values(playerList).filter(p => p.team) : []}
              prerequisiteText="Select Badges, Items, Levels, XP totals or currency totals that players must earn before this Quest becomes available."
            />
            <div className="g-space-2"></div>
            <ManageContentRewards
              type="quest"
              saveData={saveData}
              formData={formData}
              gameData={gameData}
              gameElements={gameElements}
              text="completing this Quest"
            />
            <div className="g-space-2"></div>
            <ManageContentDates
              type="quest"
              saveData={saveData}
              formData={formData}
              gameElements={gameElements}
              text="setting availability dates for this Quest"
            />

          </div>
          <div className="g-space-1"></div>
          <div className='buttons'>
            <button
              className='g-button primary'
              onClick={handleSaveQuest}>
              {

                elementPath ? "Update Quest" : "Save Quest"
              }
            </button>
          </div>
        </div>
      </div>
      <ModalAlert
        show={!!alertMessage}
        cancel={() => setAlertMessage(null)}
        message={alertMessage}
        onApprove={approveRef.current} />
      <ModalConfirm
        show={!!confirmMessage}
        cancel={() => setConfirmMessage(null)}
        message={confirmMessage}
        onApprove={approveRef.current} />
      <FullPageLoader show={loading} />
    </div>
  )
}

export default QuestForm;